import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaGraduationCap, FaChalkboardTeacher, FaArrowRight,
  FaClock, FaUser, FaUsers, FaBook, FaCheckCircle,
  FaSpinner, FaCertificate, FaRocket, FaChartLine,
  FaHeart, FaPalette, FaPlay, FaStar, FaTag,
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import SEO from '../../components/common/SEO';
import Breadcrumb from '../../components/common/Breadcrumb';
import { SectionHeader, Reveal } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { fetchCourses, fetchPlatformStats, fetchMyEnrollments } from '../../services/courseService';
import { useEnrollCourse } from '../../hooks/useEnrollCourse';
import { STATIC_COURSES, getCoursePath, withoutStaticDuplicates } from '../../config/constants/courseData';

import TeacherEnrollNote from '../../components/common/TeacherEnrollNote';
/* ─── Data ─────────────────────────────────────── */

const STUDENT_PERKS = [
  { icon: <FaChalkboardTeacher />, title: 'Expert Instructors', desc: 'Learn from seasoned creative professionals.' },
  { icon: <FaClock />,            title: 'Learn at Your Pace', desc: 'No deadlines. Study on your own schedule.' },
  { icon: <FaCertificate />,      title: 'Real Skills',        desc: 'Build a portfolio employers actually value.' },
  { icon: <FaHeart />,            title: 'Community',          desc: 'Join a network of creatives who help each other.' },
];

const TEACHER_PERKS = [
  { icon: <FaRocket />,    title: 'Launch in Minutes', desc: 'Publish a course instantly. No setup needed.' },
  { icon: <FaUsers />,     title: 'Reach Students',    desc: 'Courses appear publicly for all visitors.' },
  { icon: <FaChartLine />, title: 'Track Enrollments', desc: 'See exactly how many students enrolled.' },
  { icon: <FaStar />,      title: 'Build Your Brand',  desc: 'Your name on every course you create.' },
];

const STEPS = {
  student: [
    { n: '01', title: 'Create Account',   desc: 'Sign up in 30 seconds as a student, free forever.' },
    { n: '02', title: 'Browse Courses',   desc: 'Explore courses by professional design instructors.' },
    { n: '03', title: 'Enroll & Learn',   desc: 'Enroll with one click and start your journey today.' },
  ],
  teacher: [
    { n: '01', title: 'Register as Teacher', desc: 'Sign up with a teacher account for dashboard access.' },
    { n: '02', title: 'Create Your Course',  desc: 'Add title, description, and level. Published instantly.' },
    { n: '03', title: 'Students Enroll',     desc: 'Your course appears live and students can enroll.' },
  ],
};

/* ─── Animated counter ─────────────────────────── */
const Counter = ({ to }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!to) return;
    let v = 0;
    const step = Math.max(1, Math.ceil(to / 40));
    const t = setInterval(() => {
      v = Math.min(v + step, to);
      setN(v);
      if (v >= to) clearInterval(t);
    }, 28);
    return () => clearInterval(t);
  }, [to]);
  return <>{n}</>;
};

/* ─── Academy page ──────────────────────────────── */
const Academy = () => {
  const { user, isAuthenticated, isStudent, isTeacher, getAuthHeaders } = useAuth();
  const { enroll, enrollingId } = useEnrollCourse();
  const [tab, setTab] = useState('student');
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({ courses: 0, students: 0, teachers: 0 });
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [enrolledIds, setEnrolledIds] = useState(new Set());

  useEffect(() => {
    const calls = [fetchCourses(), fetchPlatformStats()];
    // If student is logged in, also fetch their enrollments
    if (isStudent) calls.push(fetchMyEnrollments(getAuthHeaders()));

    Promise.allSettled(calls).then(([coursesResult, statsResult, enrollmentsResult]) => {
      if (coursesResult.status === 'fulfilled') {
        setCourses(withoutStaticDuplicates(coursesResult.value).slice(0, 6));
      }
      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value);
      } else if (coursesResult.status === 'fulfilled') {
        const data = coursesResult.value;
        setStats((prev) => ({
          ...prev,
          courses: data.length,
          students: data.reduce((s, c) => s + (c.enrollment_count || 0), 0),
        }));
      }
      if (enrollmentsResult && enrollmentsResult.status === 'fulfilled') {
        setEnrolledIds(new Set(enrollmentsResult.value.map((e) => e.course_id)));
      }
    }).finally(() => setLoadingCourses(false));
  }, [isStudent, getAuthHeaders]);

  const handleEnroll = (id, title) => {
    enroll(id, title, (courseId) => {
      setEnrolledIds((prev) => new Set([...prev, courseId]));
    });
  };

  const dashTarget = isTeacher ? '/dashboard/teacher' : isStudent ? '/dashboard/student' : '/register';

  return (
    <>
      <SEO
        title="Arvix Academy | Learn Design from Industry Experts"
        description="Join Arvix Academy to master creative design skills. Enroll in courses by top instructors and launch your career in design."
        keywords="arvix academy, design courses, graphic design, learn design online"
      />

      <div className="pt-24 bg-white">
        <Breadcrumb items={[{ label: "Academy" }]} />
      </div>

      {/* ════════════════════════════════════════
          HERO
      ════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-white">
        <div
          className="texture-dots pointer-events-none absolute top-0 right-0 h-64 w-64 opacity-60 [mask-image:radial-gradient(100%_100%_at_100%_0%,black,transparent)]"
          aria-hidden="true"
        />
        <div className="relative z-10 w-full pt-8 pb-16 md:pb-20">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="tick" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.25em] text-gray-400">01</span>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-primary">
                Arvix Academy
              </p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-600 text-[10px] font-bold tracking-[0.18em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Enrolling
              </span>
            </div>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
            {/* LEFT - copy */}
            <div className="max-w-2xl">
              <Reveal delay={90}>
                <h1 className="text-balance font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-designer-dark">
                  Design skills that actually{' '}
                  <span className="relative inline-block text-designer-primary">
                    matter
                    <svg
                      className="absolute -bottom-1.5 left-0 w-full"
                      viewBox="0 0 220 12"
                      fill="none"
                      aria-hidden="true"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M3 9C60 3 150 3 217 8"
                        stroke="#E31C25"
                        strokeWidth="5"
                        strokeLinecap="round"
                        opacity="0.35"
                      />
                    </svg>
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={170}>
                <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
                  Master graphic design, branding and visual storytelling with
                  hands-on courses. Built by working creators, for the next
                  generation of them.
                </p>
              </Reveal>

              {/* CTA buttons */}
              <Reveal delay={240}>
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                  {isAuthenticated ? (
                    <Link
                      to={dashTarget}
                      className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-designer-primary hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                    >
                      <FaPlay className="w-3.5 h-3.5" aria-hidden="true" />
                      Go to Dashboard
                      <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/register"
                        className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-designer-primary hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                      >
                        <FaGraduationCap className="w-4 h-4" aria-hidden="true" />
                        Start Learning Free
                        <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                      <Link
                        to="/login"
                        className="link-sweep inline-flex items-center justify-center sm:justify-start gap-2 font-semibold text-sm text-designer-dark hover:text-designer-primary transition-colors w-fit"
                      >
                        Sign in
                        <span aria-hidden="true">→</span>
                      </Link>
                    </>
                  )}
                </div>
              </Reveal>

              {/* Trust line */}
              <Reveal delay={310}>
                <dl className="mt-10 flex items-stretch gap-6 sm:gap-8 border-t border-designer-dark/10 pt-6">
                  {[
                    { value: <><Counter to={stats.students} />+</>, label: 'Students learning' },
                    { value: <><Counter to={stats.teachers} />+</>, label: 'Expert teachers' },
                    { value: <><Counter to={stats.courses} />+</>, label: 'Live courses' },
                  ].map((s) => (
                    <div key={s.label}>
                      <dt className="sr-only">{s.label}</dt>
                      <dd className="font-display font-extrabold text-2xl sm:text-3xl text-designer-dark leading-none">
                        {s.value}
                      </dd>
                      <dd className="mt-1.5 text-xs font-medium tracking-wide text-gray-500 uppercase">
                        {s.label}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            </div>

            {/* RIGHT - role cards */}
            <Reveal delay={150}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Student card */}
                <Link
                  to={isAuthenticated && isStudent ? '/dashboard/student' : '/register'}
                  className="group relative overflow-hidden rounded-3xl border border-designer-dark/10 bg-white p-6 transition-colors duration-300 hover:border-designer-primary/40 hover:bg-red-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-designer-primary mb-4 transition-colors group-hover:bg-designer-primary group-hover:text-white">
                    <FaGraduationCap className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <h2 className="font-display font-bold text-designer-dark text-xl mb-1">For Students</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    Enroll in courses, track progress, and build real creative skills.
                  </p>
                  <span className="flex items-center gap-1.5 text-designer-primary text-sm font-semibold">
                    Join as Student
                    <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>

                {/* Teacher card */}
                <Link
                  to={isAuthenticated && isTeacher ? '/dashboard/teacher' : '/register'}
                  className="group relative overflow-hidden rounded-3xl bg-designer-dark border border-white/5 p-6 transition-colors duration-300 hover:border-designer-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_100%_0%,rgba(227,28,37,0.16),transparent)]" aria-hidden="true" />
                  <div className="relative z-10">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/15 text-white mb-4">
                      <FaChalkboardTeacher className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h2 className="font-display font-bold text-white text-xl mb-1">For Teachers</h2>
                    <p className="text-white/50 text-sm leading-relaxed mb-4">
                      Create courses, reach students, and build your brand as an instructor.
                    </p>
                    <span className="flex items-center gap-1.5 text-designer-primary text-sm font-semibold">
                      Start Teaching
                      <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          PERKS
      ════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-white">
        <SectionHeader
          index="02"
          eyebrow="Why Arvix Academy"
          title={<>Everything in one place.</>}
          lede="One platform for both sides of the classroom — learning tracks for students, publishing tools for teachers."
          layout="split"
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Student perks - light panel */}
          <Reveal className="h-full">
            <div className="h-full bg-white rounded-3xl border border-designer-dark/10 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-designer-primary">
                  <FaGraduationCap className="w-5 h-5" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-bold text-designer-dark text-sm">For Students</p>
                  <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-gray-400">Learn and grow</p>
                </div>
              </div>
              <ul className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                {STUDENT_PERKS.map((p, i) => (
                  <li key={i} className="p-4 rounded-2xl bg-designer-dark/[0.03] border border-transparent">
                    <div className="text-designer-primary mb-2 text-lg" aria-hidden="true">{p.icon}</div>
                    <h3 className="font-bold text-designer-dark text-sm mb-1">{p.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                  </li>
                ))}
              </ul>
              <Link
                to={isAuthenticated && isStudent ? '/dashboard/student' : '/register'}
                className="mt-6 flex items-center justify-center gap-2 w-full py-3 bg-designer-primary text-white text-sm font-semibold rounded-full hover:bg-red-700 transition-colors"
              >
                {isAuthenticated && isStudent ? 'Go to Dashboard' : 'Join as Student'}
                <FaArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>

          {/* Teacher perks - dark panel */}
          <Reveal delay={120} className="h-full">
            <div className="h-full bg-designer-dark rounded-3xl border border-white/5 p-6 sm:p-8 relative overflow-hidden">
              <div className="absolute inset-0 texture-grid-dark opacity-70" aria-hidden="true" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/15 text-white">
                    <FaChalkboardTeacher className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">For Teachers</p>
                    <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/40">Teach and inspire</p>
                  </div>
                </div>
                <ul className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                  {TEACHER_PERKS.map((p, i) => (
                    <li key={i} className="p-4 rounded-2xl bg-white/5 border border-white/10">
                      <div className="text-designer-primary mb-2 text-lg" aria-hidden="true">{p.icon}</div>
                      <h3 className="font-bold text-white text-sm mb-1">{p.title}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">{p.desc}</p>
                    </li>
                  ))}
                </ul>
                <Link
                  to={isAuthenticated && isTeacher ? '/dashboard/teacher' : '/register'}
                  className="mt-6 flex items-center justify-center gap-2 w-full py-3 bg-white text-designer-dark text-sm font-semibold rounded-full hover:bg-red-50 hover:text-designer-primary transition-colors"
                >
                  {isAuthenticated && isTeacher ? 'Go to Dashboard' : 'Start Teaching'}
                  <FaArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════
          HOW IT WORKS
      ════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-white">
        <SectionHeader
          index="03"
          eyebrow="The process"
          title={<>How it works.</>}
          lede="Three steps from signup to classroom — pick your side to see the path."
          layout="split"
          action={(
            <div className="inline-flex bg-designer-dark/[0.06] rounded-full p-1 gap-1" role="tablist" aria-label="How it works for">
              {['student', 'teacher'].map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50 ${
                    tab === t
                      ? 'bg-designer-dark text-white'
                      : 'text-gray-500 hover:text-designer-dark'
                  }`}
                >
                  {t === 'student' ? <FaGraduationCap className="w-3.5 h-3.5" aria-hidden="true" /> : <FaChalkboardTeacher className="w-3.5 h-3.5" aria-hidden="true" />}
                  {t === 'student' ? 'Students' : 'Teachers'}
                </button>
              ))}
            </div>
          )}
        />

        {/* Steps */}
        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS[tab].map((s, i) => (
            <Reveal key={`${tab}-${i}`} delay={i * 90} className="h-full">
              <div className="h-full rounded-3xl border border-designer-dark/10 bg-white p-7 md:p-8">
                <p className="font-mono text-[11px] font-bold tracking-[0.22em] text-designer-primary uppercase">
                  Step {s.n}
                </p>
                <h3 className="mt-3 font-display font-bold text-designer-dark text-xl">{s.title}</h3>
                <p className="mt-2 text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {!isAuthenticated && (
          <Reveal className="mt-10 text-center">
            <Link
              to="/register"
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-designer-primary hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
            >
              <HiSparkles className="w-4 h-4" aria-hidden="true" />
              Get Started, It&apos;s Free
              <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        )}
      </section>

      {/* ════════════════════════════════════════
          AVAILABLE COURSES
      ════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-white">
        <SectionHeader
          index="04"
          eyebrow="Live now"
          title={<>Available courses.</>}
          lede="Flagship programs and instructor-led courses — enroll in one click and start today."
          layout="split"
          action={(
            <Link to="/courses" className="link-sweep inline-flex items-center gap-2 font-semibold text-sm text-designer-dark hover:text-designer-primary transition-colors w-fit">
              View all courses
              <span aria-hidden="true">→</span>
            </Link>
          )}
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* ── Static courses (always shown) ── */}
          {STATIC_COURSES.map((sc) => (
            <Reveal key={sc.id} className="h-full">
              <article className="group h-full flex flex-col bg-white rounded-3xl border border-designer-dark/10 hover:border-designer-primary/40 transition-colors overflow-hidden">
                <div className="p-6 flex flex-col flex-1">
                  {/* badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-designer-primary">
                      <FaPalette className="w-5 h-5" aria-hidden="true" />
                    </div>
                    {sc.discountPercent && (
                      <span className="flex items-center gap-1 text-[10px] font-bold tracking-[0.18em] uppercase text-white bg-designer-primary px-2.5 py-1 rounded-full">
                        <FaTag className="w-2.5 h-2.5" aria-hidden="true" />
                        {sc.discountPercent}% OFF
                      </span>
                    )}
                  </div>

                  <h3 className="font-display font-bold text-designer-dark text-lg mb-1.5 leading-snug group-hover:text-designer-primary transition-colors line-clamp-2">
                    {sc.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4 flex-1">{sc.description}</p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {sc.duration && (
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-designer-dark/[0.04] px-2.5 py-1 rounded-full">
                        <FaClock className="text-designer-primary w-2.5 h-2.5" aria-hidden="true" />{sc.duration}
                      </span>
                    )}
                    {sc.level && (
                      <span className="text-xs px-2.5 py-1 bg-red-50 text-designer-primary rounded-full font-semibold">{sc.level}</span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 pb-4 mb-4 border-b border-designer-dark/10">
                    <span className="font-display text-xl font-bold text-designer-primary">{sc.fee}</span>
                    {sc.originalFee && (
                      <span className="text-sm text-gray-400 line-through">{sc.originalFee}</span>
                    )}
                  </div>

                  <Link
                    to={getCoursePath(sc)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-designer-dark text-white text-sm font-semibold rounded-full hover:bg-designer-primary transition-colors"
                  >
                    View Details <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}

          {/* ── Dynamic API courses ── */}
          {loadingCourses ? (
            <div className="flex items-center justify-center col-span-full py-16">
              <FaSpinner className="animate-spin text-designer-primary w-7 h-7" aria-hidden="true" />
            </div>
          ) : (
            courses.map((course) => (
              <Reveal key={course.id} className="h-full">
                <article className="group h-full flex flex-col bg-white rounded-3xl border border-designer-dark/10 hover:border-designer-primary/40 transition-colors overflow-hidden">
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-designer-primary mb-4">
                      <FaPalette className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="font-display font-bold text-designer-dark text-lg mb-1.5 leading-snug group-hover:text-designer-primary transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4 flex-1">{course.description}</p>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {course.duration && (
                        <span className="flex items-center gap-1 text-xs text-gray-500 bg-designer-dark/[0.04] px-2.5 py-1 rounded-full">
                          <FaClock className="text-designer-primary w-2.5 h-2.5" aria-hidden="true" />{course.duration}
                        </span>
                      )}
                      {course.level && (
                        <span className="text-xs px-2.5 py-1 bg-red-50 text-designer-primary rounded-full font-semibold">{course.level}</span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-designer-dark/[0.04] px-2.5 py-1 rounded-full">
                        <FaUsers className="text-designer-primary w-2.5 h-2.5" aria-hidden="true" />{course.enrollment_count || 0}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 pb-4 mb-4 border-b border-designer-dark/10">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-designer-primary text-[10px] font-bold text-white" aria-hidden="true">
                        {course.teacher_name?.[0]?.toUpperCase() || <FaUser className="w-2.5 h-2.5" />}
                      </span>
                      <span className="text-sm font-semibold text-gray-600">{course.teacher_name}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Link
                        to={getCoursePath(course)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 text-designer-dark border border-designer-dark/15 text-sm font-semibold rounded-full hover:border-designer-primary hover:text-designer-primary transition-colors"
                      >
                        View Details <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                      </Link>

                      {enrolledIds.has(course.id) ? (
                        <div className="flex items-center justify-center gap-2 py-2.5 text-green-600 bg-green-50 border border-green-200 rounded-full text-sm font-semibold">
                          <FaCheckCircle className="w-4 h-4" aria-hidden="true" /> Enrolled
                        </div>
                      ) : (
                        isTeacher ? (
                          <TeacherEnrollNote compact />
                        ) : (
                          <button
                            onClick={() => handleEnroll(course.id, course.title)}
                            disabled={enrollingId === course.id}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-designer-primary text-white text-sm font-semibold rounded-full hover:bg-red-700 transition-colors disabled:opacity-60"
                          >
                            {enrollingId === course.id ? (
                              <><FaSpinner className="animate-spin w-4 h-4" aria-hidden="true" /> Enrolling...</>
                            ) : (
                              <>Enroll Now <FaArrowRight className="w-3 h-3" aria-hidden="true" /></>
                            )}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))
          )}

          {/* Empty state only when no dynamic courses AND no static courses rendered */}
          {!loadingCourses && courses.length === 0 && STATIC_COURSES.length === 0 && (
            <div className="col-span-full text-center py-20 bg-designer-dark/[0.03] rounded-3xl border border-designer-dark/10">
              <FaBook className="w-10 h-10 text-gray-300 mx-auto mb-4" aria-hidden="true" />
              <p className="text-designer-dark font-semibold">No courses yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Be the first to{' '}
                <Link to="/register" className="text-designer-primary font-semibold hover:underline">
                  create a course
                </Link>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════
          CTA
      ════════════════════════════════════════ */}
      <section className="pb-20 md:pb-24 bg-white">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-designer-dark px-6 py-14 sm:px-12 md:py-16 text-center">
            <div className="absolute inset-0 texture-grid-dark opacity-70" aria-hidden="true" />
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[16rem] rounded-full bg-designer-primary/15 blur-[100px] pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative z-10 max-w-2xl mx-auto">
              <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-white/40 mb-4">
                Join Arvix Academy
              </p>
              <h2 className="text-balance font-display font-extrabold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-white">
                Your creative journey <span className="text-designer-primary">starts here</span>
              </h2>
              <p className="mt-4 text-white/50 text-base leading-relaxed">
                Free to join. No credit card. Start learning or start teaching today.
              </p>

              <div className="mt-8">
                {isAuthenticated ? (
                  <Link
                    to={isTeacher ? '/dashboard/teacher' : '/dashboard/student'}
                    className="inline-flex items-center gap-2.5 px-8 py-4 bg-designer-primary text-white text-sm font-semibold rounded-full hover:bg-red-700 transition-colors"
                  >
                    <FaPlay className="w-3.5 h-3.5" aria-hidden="true" /> Go to Dashboard
                  </Link>
                ) : (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to="/register"
                      className="group inline-flex items-center gap-2 px-8 py-4 bg-designer-primary text-white text-sm font-semibold rounded-full hover:bg-red-700 transition-colors"
                    >
                      <FaGraduationCap className="w-4 h-4" aria-hidden="true" />
                      Join as Student
                      <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold rounded-full border border-white/25 text-white hover:border-white/60 hover:bg-white/5 transition-all"
                    >
                      <FaChalkboardTeacher className="w-4 h-4" aria-hidden="true" />
                      Start Teaching
                    </Link>
                  </div>
                )}
              </div>

              {isAuthenticated ? (
                <p className="text-white/40 text-xs mt-6">
                  You are signed in as{' '}
                  <span className="text-designer-accent font-semibold">{user?.email}</span>
                </p>
              ) : (
                <p className="text-white/40 text-xs mt-6">
                  Already have an account?{' '}
                  <Link to="/login" className="text-white/70 hover:text-white underline transition-colors">
                    Sign in
                  </Link>
                </p>
              )}
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Academy;
